/**
 * Slice 56 — sends what the phone saved while there was no signal, on its own, and keeps the job's data
 * on the phone. Renders nothing; the cleaner never has to do anything.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { flushOutbox, loadOutbox, setOutboxTokenProvider } from "@/lib/offline/outbox";
import { startQueryPersistence } from "@/lib/offline/queryPersist";

const RETRY_MS = 15_000;

export function OfflineSync() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    setOutboxTokenProvider(async () => {
      const { data } = await supabase.auth.getSession();
      return data.session?.access_token ?? null;
    });
    void loadOutbox().then(() => flushOutbox());
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    void startQueryPersistence(queryClient, async () => user.id);
  }, [user?.id, queryClient]);

  useEffect(() => {
    if (!user) return;
    void flushOutbox();
    const onOnline = () => void flushOutbox();
    const onVisible = () => {
      if (document.visibilityState === "visible") void flushOutbox();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => void flushOutbox(), RETRY_MS);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [user]);

  return null;
}
